import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import { STATIONS, STATION_INDEX, STATION_BY_ID } from "./stations";
import { clippedZoneFC, offLimitsFC, buildMask, setZoneMode, setZoneOverrides } from "./geo";
import { DIVISIONS, DIV_PALETTE } from "./divisions";
import { POI_CATEGORIES, poiFC, parkFC } from "./pois";
import { geocodeArea } from "./api";
import { esc } from "./ui/util";
import type { GameClient, Pin, Note, Teammate } from "./net";

type LL = [number, number];
// Great-circle distance in miles (the "as the crow flies" measure).
function distMiles(a: LL, b: LL): number {
  const R = 3958.8, toR = Math.PI / 180;
  const dLat = (b[1] - a[1]) * toR, dLng = (b[0] - a[0]) * toR;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * toR) * Math.cos(b[1] * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
function fmtDistance(mi: number): string {
  return mi < 0.19 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(2)} mi`;
}
function centroidOf(g: GeoJSON.Geometry): LL {
  if (g.type === "Point") return g.coordinates as LL;
  const pts: number[][] = [];
  const walk = (c: unknown): void => { if (typeof (c as number[])[0] === "number") pts.push(c as number[]); else (c as unknown[]).forEach(walk); };
  walk((g as { coordinates: unknown }).coordinates);
  const n = pts.length || 1;
  return [pts.reduce((s, p) => s + p[0], 0) / n, pts.reduce((s, p) => s + p[1], 0) / n];
}
function offsetEast(c: LL, miles: number): LL {
  return [c[0] + miles / (69 * Math.cos(c[1] * Math.PI / 180)), c[1]];
}

const MANHATTAN_CENTER: [number, number] = [-73.9712, 40.7831];
const PMTILES_URL = import.meta.env.VITE_PMTILES_URL as string | undefined;

// Area airports, as targets for distance/Radar measuring when zoomed out.
export const AIRPORT_POINTS: { name: string; lat: number; lng: number }[] = [
  { name: "JFK", lat: 40.6413, lng: -73.7781 },
  { name: "LGA", lat: 40.7769, lng: -73.8740 },
  { name: "EWR", lat: 40.6895, lng: -74.1745 },
];
const AIRPORTS: GeoJSON.FeatureCollection = {
  type: "FeatureCollection",
  features: AIRPORT_POINTS.map((a) => ({ type: "Feature", geometry: { type: "Point", coordinates: [a.lng, a.lat] }, properties: { name: `✈ ${a.name}` } })),
};

const COLOR_ACTIVE = "#1f9d55";
const COLOR_ELIMINATED = "#9aa0a6";
const COLOR_SELECTED = "#f59e0b";
const COLOR_PREVIEW = "#c0392b";

// Official-ish MTA line colors for the station popup bullets.
const ROUTE_COLORS: Record<string, string> = {
  "1": "#EE352E", "2": "#EE352E", "3": "#EE352E",
  "4": "#00933C", "5": "#00933C", "6": "#00933C",
  "7": "#B933AD",
  A: "#0039A6", C: "#0039A6", E: "#0039A6",
  B: "#FF6319", D: "#FF6319", F: "#FF6319", M: "#FF6319",
  G: "#6CBE45", J: "#996633", Z: "#996633", L: "#A7A9AC",
  N: "#FCCC0A", Q: "#FCCC0A", R: "#FCCC0A", W: "#FCCC0A",
  S: "#808183",
};
export function routeBullets(routes: string[]): string {
  return routes.map((r) => {
    const bg = ROUTE_COLORS[r] || "#444";
    const fg = bg === "#FCCC0A" ? "#000" : "#fff";
    return `<span class="rb" style="background:${bg};color:${fg}">${r}</span>`;
  }).join("");
}

function rasterStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
    sources: {
      osm: { type: "raster", tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"], tileSize: 256, attribution: "© OpenStreetMap contributors", maxzoom: 19 },
    },
    layers: [{ id: "osm", type: "raster", source: "osm" }],
  };
}

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

export class GameMap {
  readonly map: maplibregl.Map;
  private selected: string | null = null;
  captureClicks = false;
  private previewedIds: string[] = [];
  private loaded = false;
  private hideEliminated = false;

  // Markers managed imperatively (draggable / labelled).
  private popup: maplibregl.Popup | null = null;
  private toolMarkers: maplibregl.Marker[] = [];
  private poiDotLayers: string[] = [];
  private measureAnchor: LL | null = null;
  private measureMovable: LL | null = null;
  private measurePopup: maplibregl.Popup | null = null;
  private measuring = false;
  private refMarker: maplibregl.Marker | null = null;
  private myLocMarker: maplibregl.Marker | null = null;
  private pinMarkers = new Map<string, maplibregl.Marker>();
  private noteMarkers = new Map<string, maplibregl.Marker>();
  private mateMarkers = new Map<string, maplibregl.Marker>();

  onStationClick: ((id: string) => void) | null = null;
  onMapClick: ((lngLat: maplibregl.LngLat) => void) | null = null;
  // When a tool pin is on the map it takes "supremacy": station zones become
  // un-tappable so a near-miss doesn't grab a station. `onToolExit` lets the
  // active tool reset its own state when the floating Exit-tool button is hit.
  onToolExit: (() => void) | null = null;
  private toolExitBtn: HTMLButtonElement | null = null;
  private toolActive() { return !!this.refMarker || this.toolMarkers.length > 0; }

  constructor(container: HTMLElement) {
    if (PMTILES_URL) maplibregl.addProtocol("pmtiles", new Protocol().tile);
    this.map = new maplibregl.Map({
      container,
      style: rasterStyle(),
      center: MANHATTAN_CENTER,
      zoom: 11.5,
      maxZoom: 19,
      // Zoom out to the tri-state region so distances to the airports (and any
      // future expansion beyond Manhattan) are measurable.
      minZoom: 8,
      maxBounds: [[-74.8, 40.35], [-73.2, 41.25]],
      attributionControl: { compact: true },
    });
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    this.map.on("load", () => { this.addLayers(); this.loaded = true; });

    // Floating "Exit tool" button — appears whenever a tool pin holds the map.
    const btn = document.createElement("button");
    btn.className = "tool-exit-btn"; btn.hidden = true; btn.textContent = "✕ Exit tool";
    btn.addEventListener("click", () => this.exitTool());
    container.appendChild(btn);
    this.toolExitBtn = btn;
  }

  private refreshToolBtn() { if (this.toolExitBtn) this.toolExitBtn.hidden = !this.toolActive(); }
  /** Drop the active tool's pins + preview and let it reset its own UI. */
  exitTool() { this.onToolExit?.(); this.clearToolMarkers(); this.clearReferenceMarker(); this.clearPreviewGeometry(); this.clearInfoLabel(); this.refreshToolBtn(); }

  private addLayers() {
    this.map.addSource("mask", { type: "geojson", data: EMPTY });
    this.map.addSource("offlimits", { type: "geojson", data: EMPTY });
    this.map.addSource("zones", { type: "geojson", data: EMPTY });
    this.map.addSource("dots", { type: "geojson", data: EMPTY });
    this.map.addSource("preview", { type: "geojson", data: EMPTY });

    // Grey mask over ineligible land.
    this.map.addLayer({ id: "mask-fill", type: "fill", source: "mask", paint: { "fill-color": "#222a26", "fill-opacity": 0.5 } });

    // Off-limits areas (orange).
    this.map.addLayer({ id: "offlimits-fill", type: "fill", source: "offlimits", paint: { "fill-color": "#e67e22", "fill-opacity": 0.22 } });
    this.map.addLayer({ id: "offlimits-line", type: "line", source: "offlimits", paint: { "line-color": "#d35400", "line-width": 1.5, "line-dasharray": [2, 1] } });

    // Active zones tinted green (eliminated/blocked are covered by the mask).
    this.map.addLayer({
      id: "zone-fill", type: "fill", source: "zones",
      paint: {
        "fill-color": ["case", ["boolean", ["feature-state", "preview"], false], COLOR_PREVIEW, ["boolean", ["feature-state", "selected"], false], COLOR_SELECTED, COLOR_ACTIVE],
        "fill-opacity": ["case", ["boolean", ["feature-state", "preview"], false], 0.3, ["boolean", ["feature-state", "eliminated"], false], 0, 0.18],
      },
    });
    this.map.addLayer({
      id: "zone-outline", type: "line", source: "zones",
      paint: {
        "line-color": ["case", ["boolean", ["feature-state", "preview"], false], COLOR_PREVIEW, ["boolean", ["feature-state", "selected"], false], COLOR_SELECTED, ["boolean", ["feature-state", "eliminated"], false], COLOR_ELIMINATED, COLOR_ACTIVE],
        "line-width": ["case", ["boolean", ["feature-state", "selected"], false], 3, ["boolean", ["feature-state", "eliminated"], false], 0.5, 1.4],
        "line-opacity": ["case", ["boolean", ["feature-state", "hidden"], false], 0, ["boolean", ["feature-state", "eliminated"], false], 0.5, 1],
      },
    });
    this.map.addLayer({
      id: "dot", type: "circle", source: "dots",
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 11, 2.5, 16, 6],
        "circle-color": ["case", ["boolean", ["feature-state", "eliminated"], false], COLOR_ELIMINATED, "#0b6b39"],
        "circle-opacity": ["case", ["boolean", ["feature-state", "hidden"], false], 0, ["boolean", ["feature-state", "eliminated"], false], 0.4, 1],
        "circle-stroke-opacity": ["case", ["boolean", ["feature-state", "hidden"], false], 0, 1],
        "circle-stroke-color": "#fff", "circle-stroke-width": 1,
      },
    });

    // Admin-division overlays (off by default; toggled from the layers panel).
    // Alternating per-region color so adjacent regions are distinct.
    const byCi = ["match", ["get", "ci"], ...DIV_PALETTE.flatMap((c, i) => [i, c]), "#888888"] as unknown as maplibregl.ExpressionSpecification;
    for (const d of DIVISIONS) {
      this.map.addSource(`div-${d.id}`, { type: "geojson", data: d.data });
      this.map.addLayer({ id: `div-${d.id}-fill`, type: "fill", source: `div-${d.id}`, layout: { visibility: "none" }, paint: { "fill-color": byCi, "fill-opacity": 0.38 } });
      this.map.addLayer({ id: `div-${d.id}-line`, type: "line", source: `div-${d.id}`, layout: { visibility: "none" }, paint: { "line-color": byCi, "line-width": 1.4, "line-opacity": 0.9 } });
      this.map.addLayer({ id: `div-${d.id}-label`, type: "symbol", source: `div-${d.id}`, layout: { visibility: "none", "text-field": ["get", "name"], "text-size": 11, "text-allow-overlap": false }, paint: { "text-color": "#1a1a1a", "text-halo-color": "#ffffff", "text-halo-width": 1.6 } });
    }

    // Airports (toggle layer; on by default).
    this.map.addSource("airports", { type: "geojson", data: AIRPORTS });
    this.map.addLayer({ id: "airport-dot", type: "circle", source: "airports", paint: { "circle-radius": 5, "circle-color": "#2563eb", "circle-stroke-color": "#fff", "circle-stroke-width": 1.5 } });
    this.map.addLayer({ id: "airport-label", type: "symbol", source: "airports", layout: { "text-field": ["get", "name"], "text-size": 12, "text-offset": [0, 1.2], "text-anchor": "top" }, paint: { "text-color": "#1d4ed8", "text-halo-color": "#fff", "text-halo-width": 1.5 } });

    // Parks as shaded polygons (measure to the edge, not a centroid).
    const parkColor = POI_CATEGORIES.find((c) => c.polygon)!.color;
    this.map.addSource("parks", { type: "geojson", data: parkFC() });
    this.map.addLayer({ id: "park-fill", type: "fill", source: "parks", layout: { visibility: "none" }, paint: { "fill-color": parkColor, "fill-opacity": 0.3 } });
    this.map.addLayer({ id: "park-line", type: "line", source: "parks", layout: { visibility: "none" }, paint: { "line-color": parkColor, "line-width": 1.5, "line-opacity": 0.95 } });
    this.map.addLayer({ id: "park-label", type: "symbol", source: "parks", minzoom: 12.5, layout: { visibility: "none", "text-field": ["get", "name"], "text-size": 11, "text-optional": true, "text-allow-overlap": false }, paint: { "text-color": "#146c43", "text-halo-color": "#fff", "text-halo-width": 1.5 } });

    // Fair-game POI categories as pins (toggle layers; all off by default).
    this.map.addSource("pois", { type: "geojson", data: poiFC() });
    const poiRadius = ["interpolate", ["linear"], ["zoom"], 9, 3.5, 13, 6.5, 16, 10] as unknown as maplibregl.ExpressionSpecification;
    for (const cat of POI_CATEGORIES) {
      if (cat.polygon) continue; // parks render as polygons above
      const filter = ["==", ["get", "cat"], cat.id] as unknown as maplibregl.FilterSpecification;
      this.map.addLayer({ id: `poi-${cat.id}-dot`, type: "circle", source: "pois", filter, layout: { visibility: "none" }, paint: { "circle-radius": poiRadius, "circle-color": cat.color, "circle-stroke-color": "#fff", "circle-stroke-width": 2, "circle-opacity": 0.95 } });
      this.map.addLayer({ id: `poi-${cat.id}-label`, type: "symbol", source: "pois", filter, minzoom: 12.5, layout: { visibility: "none", "text-field": ["get", "name"], "text-size": 11, "text-offset": [0, 0.9], "text-anchor": "top", "text-optional": true, "text-allow-overlap": false }, paint: { "text-color": cat.color, "text-halo-color": "#fff", "text-halo-width": 1.5 } });
    }
    this.poiDotLayers = POI_CATEGORIES.filter((c) => !c.polygon).map((c) => `poi-${c.id}-dot`);

    // Measure line (POI → dropped pin / address; "as the crow flies").
    this.map.addSource("measure", { type: "geojson", data: EMPTY });
    this.map.addLayer({ id: "measure-line", type: "line", source: "measure", paint: { "line-color": "#1d4ed8", "line-width": 2.5, "line-dasharray": [1.6, 1] } });

    // Matching-assist overlay (stacked colored "how close am I" lines + targets).
    this.map.addSource("assist", { type: "geojson", data: EMPTY });
    this.map.addLayer({ id: "assist-line", type: "line", source: "assist", filter: ["==", ["geometry-type"], "LineString"], paint: { "line-color": ["get", "color"], "line-width": 2.4, "line-dasharray": [1.6, 1], "line-opacity": 0.9 } });
    this.map.addLayer({ id: "assist-dot", type: "circle", source: "assist", filter: ["==", ["geometry-type"], "Point"], paint: { "circle-radius": ["case", ["get", "closest"], 6, 4.5], "circle-color": ["get", "color"], "circle-stroke-color": "#fff", "circle-stroke-width": 1.6 } });
    this.map.addLayer({ id: "assist-label", type: "symbol", source: "assist", filter: ["==", ["geometry-type"], "Point"], layout: { "text-field": ["get", "label"], "text-size": 11, "text-offset": [0, 1], "text-anchor": "top", "text-allow-overlap": true }, paint: { "text-color": ["get", "color"], "text-halo-color": "#fff", "text-halo-width": 1.7 } });

    // Preview (tool) geometry.
    this.map.addLayer({ id: "preview-fill", type: "fill", source: "preview", filter: ["==", ["geometry-type"], "Polygon"], paint: { "fill-color": COLOR_PREVIEW, "fill-opacity": 0.08 } });
    this.map.addLayer({ id: "preview-line", type: "line", source: "preview", filter: ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]], paint: { "line-color": COLOR_PREVIEW, "line-width": 2, "line-dasharray": [2, 1] } });

    this.map.on("click", (e) => {
      if (this.captureClicks) { this.onMapClick?.(e.lngLat); return; }
      // Tool pins have UI supremacy: while one is up, the map (stations, zones,
      // POIs) is not selectable — only the tool's own markers respond.
      if (this.toolActive()) return;
      // A fair-game POI pin → measuring popup.
      const poi = this.poiDotLayers.length ? this.map.queryRenderedFeatures(e.point, { layers: this.poiDotLayers })[0] : undefined;
      if (poi) { const c = (poi.geometry as GeoJSON.Point).coordinates as LL; this.showPoiMeasurePopup(String(poi.properties?.name ?? "this location"), c); return; }
      // A park polygon → "measure to nearest edge" guidance.
      const park = this.map.queryRenderedFeatures(e.point, { layers: ["park-fill"] })[0];
      if (park) { this.showParkMeasureMsg(String(park.properties?.name ?? "this park"), [e.lngLat.lng, e.lngLat.lat]); return; }
      const f = this.map.queryRenderedFeatures(e.point, { layers: ["zone-fill", "dot"] })[0];
      if (f?.properties?.id) { const id = f.properties.id as string; this.showStationPopup(id); this.onStationClick?.(id); }
      else this.onMapClick?.(e.lngLat);
    });
  }

  whenReady(fn: () => void) {
    if (this.loaded) fn();
    else this.map.on("load", fn);
  }

  // ---- Data refresh from the game client ----------------------------------
  refresh(client: GameClient) {
    if (!this.loaded) { this.map.once("load", () => this.refresh(client)); return; }
    setZoneMode(client.config.zoneMode);
    setZoneOverrides(client.zoneOverrides, client.zoneOverridesV);
    const off = client.offLimits;
    (this.map.getSource("zones") as maplibregl.GeoJSONSource).setData(clippedZoneFC(off));
    (this.map.getSource("offlimits") as maplibregl.GeoJSONSource).setData(offLimitsFC(off));
    (this.map.getSource("dots") as maplibregl.GeoJSONSource).setData(this.dotsFC());

    const eligible = STATIONS.filter((s) => client.statusOf(s.id) === "active").map((s) => s.id);
    (this.map.getSource("mask") as maplibregl.GeoJSONSource).setData({ type: "FeatureCollection", features: [buildMask(eligible, off)] });

    for (const s of STATIONS) {
      const eliminated = client.statusOf(s.id) === "eliminated";
      const hidden = this.hideEliminated && eliminated;
      const fid = STATION_INDEX[s.id];
      this.map.setFeatureState({ source: "zones", id: fid }, { eliminated, hidden });
      this.map.setFeatureState({ source: "dots", id: fid }, { eliminated, hidden });
    }
  }

  setHideEliminated(hide: boolean, client: GameClient) {
    this.hideEliminated = hide;
    this.refresh(client);
  }

  /**
   * Freehand finger/mouse draw: disables map panning, collects the dragged path,
   * fires onUpdate while drawing and onComplete on release. Returns a teardown.
   */
  freehandDraw(onUpdate: (pts: [number, number][]) => void, onComplete: (pts: [number, number][]) => void): () => void {
    this.map.dragPan.disable();
    this.captureClicks = true;
    this.map.getCanvas().style.cursor = "crosshair";
    let pts: [number, number][] = [];
    let drawing = false;
    const down = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => { drawing = true; pts = [[e.lngLat.lng, e.lngLat.lat]]; };
    const move = (e: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => { if (!drawing) return; pts.push([e.lngLat.lng, e.lngLat.lat]); onUpdate([...pts]); };
    const up = () => { if (!drawing) return; drawing = false; if (pts.length >= 2) onComplete([...pts]); };
    this.map.on("mousedown", down); this.map.on("mousemove", move); this.map.on("mouseup", up);
    this.map.on("touchstart", down); this.map.on("touchmove", move); this.map.on("touchend", up);
    return () => {
      this.map.off("mousedown", down); this.map.off("mousemove", move); this.map.off("mouseup", up);
      this.map.off("touchstart", down); this.map.off("touchmove", move); this.map.off("touchend", up);
      this.map.dragPan.enable();
      this.captureClicks = false;
      this.map.getCanvas().style.cursor = "";
    };
  }

  /**
   * Zone editor: drops a draggable handle on every corner of a zone polygon.
   * Dragging reshapes it (live preview); onChange fires with the new geometry
   * on each drop. Returns a teardown that removes the handles + preview.
   */
  private editHandles: maplibregl.Marker[] = [];
  private editing = false;
  startZoneEdit(geometry: GeoJSON.Geometry, onChange: (g: GeoJSON.Geometry) => void): () => void {
    this.stopZoneEdit();
    // Lock map panning so a near-miss drags the corner, not the whole map.
    this.map.dragPan.disable();
    this.editing = true;
    type Part = { outer: number[][]; holes: number[][][] };
    const parts: Part[] =
      geometry.type === "MultiPolygon"
        ? (geometry.coordinates as number[][][][]).map((p) => ({ outer: p[0], holes: p.slice(1) }))
        : geometry.type === "Polygon"
        ? [{ outer: (geometry.coordinates as number[][][])[0], holes: (geometry.coordinates as number[][][]).slice(1) }]
        : [];
    const toGeometry = (): GeoJSON.Geometry =>
      parts.length === 1
        ? { type: "Polygon", coordinates: [parts[0].outer, ...parts[0].holes] }
        : { type: "MultiPolygon", coordinates: parts.map((p) => [p.outer, ...p.holes]) };
    const preview = () => this.setPreviewGeometry([{ type: "Feature", geometry: toGeometry(), properties: {} }]);

    for (const part of parts) {
      const ring = part.outer;
      const last = ring.length - 1;
      const closed = last > 0 && ring[0][0] === ring[last][0] && ring[0][1] === ring[last][1];
      const nVerts = closed ? last : ring.length;
      for (let vi = 0; vi < nVerts; vi++) {
        const el = document.createElement("div");
        el.className = "zone-handle";
        const mk = new maplibregl.Marker({ element: el, draggable: true }).setLngLat(ring[vi] as [number, number]).addTo(this.map);
        const i = vi;
        mk.on("drag", () => {
          const ll = mk.getLngLat();
          ring[i] = [ll.lng, ll.lat];
          if (closed && i === 0) ring[last] = [ll.lng, ll.lat];
          preview();
        });
        mk.on("dragend", () => onChange(toGeometry()));
        this.editHandles.push(mk);
      }
    }
    preview();
    return () => this.stopZoneEdit();
  }
  stopZoneEdit() {
    this.editHandles.forEach((m) => m.remove());
    this.editHandles = [];
    this.clearPreviewGeometry();
    if (this.editing) { this.map.dragPan.enable(); this.editing = false; }
  }

  private dotsFC(): GeoJSON.FeatureCollection {
    return {
      type: "FeatureCollection",
      features: STATIONS.map((s) => ({ type: "Feature", id: STATION_INDEX[s.id], geometry: { type: "Point", coordinates: [s.lng, s.lat] }, properties: { id: s.id, name: s.name } })),
    };
  }

  setLayerVisible(id: string, visible: boolean) {
    if (this.map.getLayer(id)) this.map.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
  }

  // ---- admin-division select pins (one per district) ----------------------
  // When a division overlay is on, drop a tappable pin at each district's
  // centroid so the user can select it and eliminate / isolate that district.
  private divPins: Record<string, maplibregl.Marker[]> = {};
  setDivisionPins(divId: string, on: boolean, onPick: (name: string, geometry: GeoJSON.Geometry, at: LL) => void) {
    (this.divPins[divId] || []).forEach((m) => m.remove());
    this.divPins[divId] = [];
    if (!on) return;
    const d = DIVISIONS.find((x) => x.id === divId);
    if (!d) return;
    for (const f of d.data.features) {
      if (!f.geometry) continue;
      let c: LL;
      try { c = centroidOf(f.geometry); } catch { continue; }
      const name = String(f.properties?.name ?? "District");
      const el = document.createElement("div");
      el.className = "div-pin";
      el.textContent = "⛳";
      el.title = name;
      const mk = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(c).addTo(this.map);
      el.addEventListener("click", (e) => { e.stopPropagation(); onPick(name, f.geometry, c); });
      this.divPins[divId].push(mk);
    }
  }

  /** A small popup of action buttons at a point (district eliminate / isolate). */
  showActionPopup(coord: LL, title: string, actions: { label: string; danger?: boolean; fn: () => void }[]) {
    this.popup?.remove();
    this.popup = new maplibregl.Popup({ offset: 14, closeButton: true, className: "measure-pop-wrap" })
      .setLngLat(coord)
      .setHTML(`<div class="measure-pop"><div class="mp-name">${esc(title)}</div><div class="mp-btns">${actions.map((a, i) => `<button class="mp-btn ${a.danger ? "danger" : ""}" data-act="${i}">${esc(a.label)}</button>`).join("")}</div></div>`)
      .addTo(this.map);
    const el = this.popup.getElement();
    actions.forEach((a, i) => el.querySelector(`[data-act="${i}"]`)?.addEventListener("click", () => { this.popup?.remove(); a.fn(); }));
  }

  // ---- selection / navigation ---------------------------------------------
  select(id: string | null) {
    if (!this.loaded) { this.map.once("load", () => this.select(id)); return; }
    if (this.selected) this.map.setFeatureState({ source: "zones", id: STATION_INDEX[this.selected] }, { selected: false });
    this.selected = id;
    if (id) this.map.setFeatureState({ source: "zones", id: STATION_INDEX[id] }, { selected: true });
  }
  flyToStation(id: string) {
    const s = STATION_BY_ID[id];
    if (s) this.map.flyTo({ center: [s.lng, s.lat], zoom: 16, speed: 1.2 });
  }

  /** Popup naming the station + its lines (shown on click, on every map). */
  showStationPopup(id: string) {
    const s = STATION_BY_ID[id];
    if (!s) return;
    this.popup?.remove();
    this.popup = new maplibregl.Popup({ offset: 12, closeButton: true, className: "stn-pop" })
      .setLngLat([s.lng, s.lat])
      .setHTML(`<div class="stn-popup"><div class="stn-name">${s.name}</div><div class="stn-routes">${routeBullets(s.routes)}</div></div>`)
      .addTo(this.map);
  }

  // ---- measure tool (POI → dropped pin / address) -------------------------
  /** Click a POI pin: offer to measure to it via a dropped pin or an address. */
  showPoiMeasurePopup(name: string, coord: LL) {
    this.endMeasure();
    this.popup?.remove();
    this.popup = new maplibregl.Popup({ offset: 14, closeButton: true, className: "measure-pop-wrap" })
      .setLngLat(coord)
      .setHTML(`<div class="measure-pop"><div class="mp-name">${esc(name)}</div><div class="mp-sub">Measure to this location</div>
        <div class="mp-btns"><button class="mp-btn" id="mp-pin">📍 Drop a pin</button><button class="mp-btn" id="mp-addr">📫 Enter address</button></div></div>`)
      .addTo(this.map);
    const el = this.popup.getElement();
    el.querySelector("#mp-pin")?.addEventListener("click", () => {
      this.popup?.remove();
      const c = this.map.getCenter();
      const center: LL = [c.lng, c.lat];
      const movable = distMiles(center, coord) < 0.06 ? offsetEast(coord, 0.35) : center;
      this.startMeasure(coord, movable);
    });
    el.querySelector("#mp-addr")?.addEventListener("click", async () => {
      const q = window.prompt(`Measure from ${name} to which address or place?`);
      if (!q || !q.trim()) return;
      try {
        const { geometry } = await geocodeArea(q.trim());
        this.popup?.remove();
        this.startMeasure(coord, centroidOf(geometry), true);
      } catch { window.alert("Couldn't find that address."); }
    });
  }

  /** Click a park polygon: parks are measured to their nearest edge, not a point. */
  showParkMeasureMsg(name: string, lngLat: LL) {
    this.endMeasure();
    this.popup?.remove();
    this.popup = new maplibregl.Popup({ offset: 14, closeButton: true, className: "measure-pop-wrap" })
      .setLngLat(lngLat)
      .setHTML(`<div class="measure-pop"><div class="mp-name">🌳 ${esc(name)}</div><div class="mp-sub">Measure to the nearest edge from your location.</div></div>`)
      .addTo(this.map);
  }

  /** Draw a live "crow-flies" line from a locked anchor to a draggable point. */
  startMeasure(anchor: LL, movable: LL, fit = false) {
    this.endMeasure();
    this.measuring = true;
    this.measureAnchor = anchor;
    this.measureMovable = movable;
    this.addToolMarker(anchor, "#1d4ed8", false);
    this.addToolMarker(movable, "#c0392b", true, (ll) => { this.measureMovable = ll; this.updateMeasure(); });
    this.measurePopup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, className: "measure-dist" });
    this.measurePopup.on("close", () => { if (this.measuring) this.endMeasure(); });
    this.updateMeasure();
    if (fit) this.map.fitBounds([anchor, movable], { padding: 90, maxZoom: 15, duration: 600 });
  }
  private updateMeasure() {
    const a = this.measureAnchor, b = this.measureMovable;
    if (!a || !b) return;
    (this.map.getSource("measure") as maplibregl.GeoJSONSource | undefined)?.setData({
      type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "LineString", coordinates: [a, b] }, properties: {} }],
    });
    const mid: LL = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    this.measurePopup?.setLngLat(mid).setHTML(`<div class="dist">${fmtDistance(distMiles(a, b))}</div>`).addTo(this.map);
  }
  endMeasure() {
    if (!this.measuring && !this.measurePopup) return;
    this.measuring = false;
    this.clearToolMarkers();
    (this.map.getSource("measure") as maplibregl.GeoJSONSource | undefined)?.setData(EMPTY);
    this.measureAnchor = this.measureMovable = null;
    const p = this.measurePopup; this.measurePopup = null; p?.remove();
  }

  // ---- matching-assist overlay --------------------------------------------
  setAssistOverlay(features: GeoJSON.Feature[]) {
    (this.map.getSource("assist") as maplibregl.GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features });
  }
  clearAssistOverlay() { this.setAssistOverlay([]); }
  /** The "measure from here" reference marker (a home pin; draggable in pin mode). */
  setReferenceMarker(ll: LL, draggable: boolean, onMove?: (ll: LL) => void) {
    this.clearReferenceMarker();
    const el = document.createElement("div");
    el.className = "ref-pin";
    this.refMarker = new maplibregl.Marker({ element: el, draggable, anchor: "center" }).setLngLat(ll).addTo(this.map);
    if (draggable && onMove) this.refMarker.on("dragend", () => { const l = this.refMarker!.getLngLat(); onMove([l.lng, l.lat]); });
    this.refreshToolBtn();
  }
  clearReferenceMarker() { this.refMarker?.remove(); this.refMarker = null; this.refreshToolBtn(); }
  /** A persistent, draggable "you are here" pin. setMyLocation re-drops it at `ll`
   * (used both to place it from GPS and to snap it back); dragging fires onMove. */
  setMyLocation(ll: LL, onMove?: (ll: LL) => void) {
    if (this.myLocMarker) { this.myLocMarker.setLngLat(ll); return; }
    const el = document.createElement("div");
    el.className = "my-loc-pin";
    el.title = "You are here (GPS) — drag to test a spot; tap ◎ to snap back";
    this.myLocMarker = new maplibregl.Marker({ element: el, draggable: true, anchor: "center" }).setLngLat(ll).addTo(this.map);
    if (onMove) this.myLocMarker.on("drag", () => { const l = this.myLocMarker!.getLngLat(); onMove([l.lng, l.lat]); });
  }
  /** Force the pin back to a coordinate (revert-to-GPS), creating it if needed. */
  snapMyLocation(ll: LL) { if (this.myLocMarker) this.myLocMarker.setLngLat(ll); else this.setMyLocation(ll); }
  myLocation(): LL | null { if (!this.myLocMarker) return null; const l = this.myLocMarker.getLngLat(); return [l.lng, l.lat]; }
  clearMyLocation() { this.myLocMarker?.remove(); this.myLocMarker = null; }
  panTo(ll: LL, maxZoom = 14) { this.map.flyTo({ center: ll, zoom: Math.min(this.map.getZoom() < 11 ? maxZoom : this.map.getZoom(), 16), speed: 1.2 }); }

  // Fly so the point sits in the visible map ABOVE the bottom sheet (the sheet
  // covers the lower ~half), centring it in the top portion of the screen.
  flyAboveSheet(ll: LL, zoom = 14) {
    this.map.flyTo({ center: ll, zoom: Math.max(this.map.getZoom(), zoom), padding: { top: 0, left: 0, right: 0, bottom: Math.round(window.innerHeight * 0.45) }, speed: 1.2 });
  }

  // A free-floating label on the map (distance next to a line origin, a ring's
  // radius near its centre) — independent of the POI-measure popup machinery.
  private labelPopup: maplibregl.Popup | null = null;
  setInfoLabel(ll: LL, text: string) {
    if (!this.labelPopup) this.labelPopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, className: "measure-dist", offset: 14 });
    this.labelPopup.setLngLat(ll).setHTML(`<div class="dist">${text}</div>`).addTo(this.map);
  }
  clearInfoLabel() { this.labelPopup?.remove(); this.labelPopup = null; }

  // ---- preview overlay -----------------------------------------------------
  setPreviewGeometry(features: GeoJSON.Feature[]) {
    (this.map.getSource("preview") as maplibregl.GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features });
  }
  clearPreviewGeometry() { this.setPreviewGeometry([]); }
  setPreviewEliminations(ids: string[]) {
    for (const id of this.previewedIds) this.map.setFeatureState({ source: "zones", id: STATION_INDEX[id] }, { preview: false });
    this.previewedIds = ids;
    for (const id of ids) this.map.setFeatureState({ source: "zones", id: STATION_INDEX[id] }, { preview: true });
  }
  clearPreviewEliminations() { this.setPreviewEliminations([]); }

  // ---- transient draggable tool pins --------------------------------------
  // `centered` swaps the teardrop pin (whose tip marks the point) for a round
  // handle centered exactly on the coordinate — used where the marker must sit
  // ON a drawn line (e.g. the Cut endpoints), and optionally labelled (A/B).
  addToolMarker(lngLat: [number, number], color: string, draggable: boolean, onMove?: (ll: [number, number]) => void, centered = false, label = ""): maplibregl.Marker {
    let m: maplibregl.Marker;
    if (centered) {
      const el = document.createElement("div");
      el.className = "tool-handle";
      el.style.background = color;
      if (label) { el.textContent = label; el.classList.add("lbl"); }
      m = new maplibregl.Marker({ element: el, draggable, anchor: "center" }).setLngLat(lngLat).addTo(this.map);
    } else {
      m = new maplibregl.Marker({ color, draggable }).setLngLat(lngLat).addTo(this.map);
    }
    if (draggable && onMove) m.on("drag", () => { const ll = m.getLngLat(); onMove([ll.lng, ll.lat]); });
    this.toolMarkers.push(m);
    this.refreshToolBtn();
    return m;
  }
  clearToolMarkers() { this.toolMarkers.forEach((m) => m.remove()); this.toolMarkers = []; this.refreshToolBtn(); }

  // ---- persistent synced markers ------------------------------------------
  syncPins(pins: Pin[], onMove: (id: string, lng: number, lat: number) => void, onRemove: (id: string) => void) {
    this.diffMarkers(this.pinMarkers, pins, (p) => {
      const el = document.createElement("div");
      el.className = "map-pin";
      el.title = "Drag to move · double-click to remove";
      const m = new maplibregl.Marker({ element: el, draggable: true }).setLngLat([p.lng, p.lat]).addTo(this.map);
      m.on("dragend", () => { const ll = m.getLngLat(); onMove(p.id, ll.lng, ll.lat); });
      el.addEventListener("dblclick", (e) => { e.stopPropagation(); onRemove(p.id); });
      return m;
    }, (m, p) => m.setLngLat([p.lng, p.lat]));
  }

  syncNotes(notes: Note[], onMove: (id: string, lng: number, lat: number) => void, onEdit: (id: string) => void, onRemove: (id: string) => void) {
    this.diffMarkers(this.noteMarkers, notes, (n) => {
      const el = document.createElement("div");
      el.className = "map-note";
      el.textContent = n.text;
      el.title = "Drag to move · click to edit · double-click to remove";
      const m = new maplibregl.Marker({ element: el, draggable: true, anchor: "bottom" }).setLngLat([n.lng, n.lat]).addTo(this.map);
      m.on("dragend", () => { const ll = m.getLngLat(); onMove(n.id, ll.lng, ll.lat); });
      el.addEventListener("click", (e) => { e.stopPropagation(); onEdit(n.id); });
      el.addEventListener("dblclick", (e) => { e.stopPropagation(); onRemove(n.id); });
      return m;
    }, (m, n) => { m.setLngLat([n.lng, n.lat]); (m.getElement().textContent = n.text); });
  }

  syncTeammates(mates: Teammate[], selfId: string) {
    const visible = mates.filter((m) => m.id !== selfId);
    this.diffMarkers(this.mateMarkers, visible, (t) => {
      const el = document.createElement("div");
      el.className = "map-mate";
      el.innerHTML = `<span class="dot"></span><span class="lbl">${t.name}</span>`;
      return new maplibregl.Marker({ element: el, anchor: "left" }).setLngLat([t.lng, t.lat]).addTo(this.map);
    }, (m, t) => m.setLngLat([t.lng, t.lat]));
  }

  private diffMarkers<T extends { id: string }>(store: Map<string, maplibregl.Marker>, items: T[], make: (item: T) => maplibregl.Marker, update: (m: maplibregl.Marker, item: T) => void) {
    const seen = new Set<string>();
    for (const item of items) {
      seen.add(item.id);
      const existing = store.get(item.id);
      if (existing) update(existing, item);
      else store.set(item.id, make(item));
    }
    for (const [id, m] of store) if (!seen.has(id)) { m.remove(); store.delete(id); }
  }
}
